import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Close, ExpandMore, OpenInNew, ReceiptLong } from '@mui/icons-material'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'
import { Activity, Client, Enrollment, EnrollmentPaymentStatus } from '../../../../pure-js/types/GrooverTypes'
import { getUserPaymentStatus } from '../../../../pure-js/libs/EnrollmentHelper'
import { getCurrencyFromEnrollment } from '../../../../pure-js/libs/currencyHelper'
import { addSsnForEnrollmentUser } from '../../libs/CloudFunctionsApiHandler'
import { downloadReceiptPdf } from '../../libs/downloadReceiptPdf'
import { useReceiptSsn } from '../../hooks/useReceiptSsn'
import { ReceiptSsnInputForm } from '../../components/ReceiptSsnInputForm'
import GrooverText from '../../components/GrooverText'
import QRCodeElement from '../../components/QRCodeElement'
import { QrExpandedDialog } from '../../components/qr/QrExpandedDialog'
import { formatGrooverText } from '../../libs/GrooverTextRepository'
import {
  formatActivityStartDate,
  formatActivityStartTime,
  getActivityPublicPath,
  getActivityStartDateTime,
  getEnrollmentActivityStatus,
  getEnrollmentLocationName,
  getEnrollmentOccasions,
  getEnrollmentQrValue,
  getEnrollmentStatusLabel,
  getEnrollmentTitle,
  getNextOccasionDateTime,
  getPaymentStatusTextKey,
  canShowPortalEnrollmentPayNow,
  getPayForForPortalUser,
  getPortalEnrollmentPaymentPath,
  getPortalEnrollmentChildNames,
  getPortalHardCouplePartnerName,
  handlePortalPayNowClick,
  getPortalPaymentTotals,
  getPortalPriceLines,
  getPortalReceiptPayments,
  getPortalReceiptRowKey,
  getRelativeUpcomingUnit,
  hasEnrollmentDescription,
  isPortalActivityPaid
} from './portalEnrollmentHelpers'
import type { PortalEnrollmentGetText } from './PortalEnrollmentCard'
import {
  ActivityCollapseButton,
  ActivityCollapseCopy,
  ActivityCollapsedSummary,
  ActivityExpandIcon,
  ActivityExpandedDetails,
  ActivityExternalLink,
  ActivityHeaderRow,
  ActivityList,
  ActivityListRow,
  ActivityListTitle,
  ReceiptList,
  ReceiptListRow,
  ReceiptsSection,
  ReceiptsSectionHeader,
  ActivityMetaGrid,
  ActivityMetaItem,
  ActivityMetaValue,
  ActivityRowActions,
  ActivityStatusPills,
  DescriptionModal,
  DescriptionModalTitle,
  DescriptionOverlay,
  DetailCard,
  DetailCardCloseButton,
  DetailCardContent,
  DetailCardTitle,
  DetailCloseFooter,
  DetailColumns,
  DetailMainColumn,
  DetailOverlay,
  DetailQRSection,
  DetailQRWrapper,
  DetailSchoolSubtitle,
  DetailSideColumn,
  DetailSideSection,
  DetailTopBar,
  DownloadPdfButton,
  LocationText,
  OpenDescriptionButton,
  PayEnrollmentLinkBlock,
  RichDescription,
  ReceiptActions,
  ReceiptBox,
  ReceiptRef,
  ReceiptRow,
  ReceiptRowLabel,
  ReceiptRowValue,
  ReceiptRows,
  ReceiptSellerBlock,
  ReceiptSellerLabel,
  ReceiptSellerValue,
  SectionLabel,
  SsnToggle,
  StatusPill
} from './PortalEnrollmentsPage.styles'

const RECEIPT_ELEMENT_ID = 'portal-enrollment-receipt'

const formatPrice = (cents: number, currency: string): string => `${(cents / 100).toFixed(2)} ${currency}`

interface PortalEnrollmentDetailCardProps {
  enrollment: Enrollment
  client: Client | null
  enrollmentUserId: string
  getText: PortalEnrollmentGetText
  locale: string
  onClose: () => void
  isViewAs?: boolean
}

export const PortalEnrollmentDetailCard: React.FC<PortalEnrollmentDetailCardProps> = ({
  enrollment,
  client,
  enrollmentUserId,
  getText,
  locale,
  onClose,
  isViewAs = false
}) => {
  const [showQrExpanded, setShowQrExpanded] = useState(false)
  const [descriptionActivityId, setDescriptionActivityId] = useState<string | null>(null)
  const [expandedActivityIds, setExpandedActivityIds] = useState<string[]>([])
  const [expandedReceiptKeys, setExpandedReceiptKeys] = useState<string[]>([])
  const qrCodeValue = getEnrollmentQrValue(enrollment, client)
  const title = getEnrollmentTitle(enrollment, getText('portalEnrollments'))
  const partnerName = getPortalHardCouplePartnerName(enrollment, enrollmentUserId)
  const childNames = getPortalEnrollmentChildNames(enrollment)
  const activities = enrollment.activities ?? []
  const descriptionActivity = activities.find((activity) => activity.id === descriptionActivityId)
  const payFor = getPayForForPortalUser(enrollment, enrollmentUserId)
  const priceLines = useMemo(() => getPortalPriceLines(enrollment, payFor), [enrollment, payFor])
  const lang = locale.startsWith('sv') ? 'sv' : locale.startsWith('nb') ? 'no' : 'en'
  const paymentStatus = getUserPaymentStatus(
    { ...enrollment, priceDetails: enrollment.priceDetails ?? [] },
    payFor,
    client ?? undefined
  )
  const currency = getCurrencyFromEnrollment(enrollment, client)
  const paymentPath = canShowPortalEnrollmentPayNow(enrollment)
    ? getPortalEnrollmentPaymentPath(enrollment, { enrollmentUserId, lang })
    : null
  const paymentTotals = getPortalPaymentTotals(paymentStatus)
  const showPayLink = !!paymentPath && paymentTotals.remaining > 0
  const receiptPayments = useMemo(() => getPortalReceiptPayments(enrollment), [enrollment])

  const isPartner = enrollment.partnerUser?.id === enrollmentUserId
  const userSsn = isPartner ? enrollment.partnerUser?.ssn : enrollment.mainUser?.ssn
  const { showSsnInput, setShowSsnInput, savedSsn, isSavingSsn, ssnFormProps, handleSaveSsn } = useReceiptSsn(
    (ssnVal) =>
      addSsnForEnrollmentUser({
        clientId: enrollment.clientId,
        enrollmentId: enrollment.id,
        userType: isPartner ? 'partnerUser' : 'mainUser',
        ssn: ssnVal
      }),
    currency
  )
  const displaySsn = savedSsn ?? userSsn

  const vatRate = activities[0]?.vat ?? client?.defaultVAT
  const vatMultiplier = vatRate !== undefined && vatRate > 0 ? 1 + vatRate / 100 : 1

  const handleDownload = useCallback(
    (elementId: string) => {
      downloadReceiptPdf(elementId, `${client?.name || 'Enrollment'}_Receipt_${enrollment.id}.pdf`)
    },
    [client?.name, enrollment.id]
  )

  const toggleActivity = useCallback((activityId: string) => {
    setExpandedActivityIds((current) =>
      current.includes(activityId) ? current.filter((id) => id !== activityId) : [...current, activityId]
    )
  }, [])

  const toggleReceipt = useCallback((receiptKey: string) => {
    setExpandedReceiptKeys((current) =>
      current.includes(receiptKey) ? current.filter((key) => key !== receiptKey) : [...current, receiptKey]
    )
  }, [])

  const nextOccasionLabel = (activity: Activity): string | null => {
    const next = getNextOccasionDateTime(activity)
    if (!next) return null
    const relative = getRelativeUpcomingUnit(next)
    if (!relative) return null
    if (relative.unit === 'days') {
      return relative.value === 1
        ? getText('portalEnrollmentNextOccasionDay')
        : formatGrooverText(getText('portalEnrollmentNextOccasionDays'), [String(relative.value)])
    }
    if (relative.unit === 'hours') {
      return relative.value === 1
        ? getText('portalEnrollmentNextOccasionHour')
        : formatGrooverText(getText('portalEnrollmentNextOccasionHours'), [String(relative.value)])
    }
    return relative.value === 1
      ? getText('portalEnrollmentNextOccasionMinute')
      : formatGrooverText(getText('portalEnrollmentNextOccasionMinutes'), [String(relative.value)])
  }

  useEffect(() => {
    if (!descriptionActivityId) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      setDescriptionActivityId(null)
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [descriptionActivityId])

  return (
    <>
      <DetailOverlay role="dialog" aria-modal="true" aria-labelledby="portal-enrollment-detail-title" onClick={onClose}>
        <DetailCard onClick={(e) => e.stopPropagation()}>
          <DetailCardCloseButton type="button" onClick={onClose} aria-label={getText('portalClose')}>
            <Close style={{ fontSize: 20 }} aria-hidden />
          </DetailCardCloseButton>
          <DetailCardContent>
            <DetailTopBar>
              <StatusPill $status={enrollment.status} $prominent>
                {getEnrollmentStatusLabel(enrollment.status, getText)}
              </StatusPill>
              <StatusPill $status={enrollment.paymentStatus || EnrollmentPaymentStatus.Unpaid} $prominent>
                {getText(getPaymentStatusTextKey(enrollment.paymentStatus))}
              </StatusPill>
            </DetailTopBar>
            <DetailCardTitle id="portal-enrollment-detail-title">
              {activities.length > 1
                ? formatGrooverText(getText('portalEnrollmentIncludesActivities'), [String(activities.length)])
                : title}
            </DetailCardTitle>
            {(client?.name || childNames || partnerName) && (
              <DetailSchoolSubtitle>
                {client?.name && <div>{client.name}</div>}
                {childNames && (
                  <div>{formatGrooverText(getText('portalEnrollmentRegisteredForChild'), [childNames])}</div>
                )}
                {partnerName && (
                  <div>{formatGrooverText(getText('portalEnrollmentRegisteredTogether'), [partnerName])}</div>
                )}
              </DetailSchoolSubtitle>
            )}

            <DetailColumns>
              <DetailMainColumn>
                <ActivityList>
                  {activities.map((activity) => {
                    const locationName = getEnrollmentLocationName(activity, client)
                    const start = getActivityStartDateTime(activity)
                    const nextLabel = nextOccasionLabel(activity)
                    const activityStatus = getEnrollmentActivityStatus(enrollment, activity.id)
                    const activityPaid = isPortalActivityPaid(enrollment, activity.id, payFor)
                    const expanded = expandedActivityIds.includes(activity.id)
                    const detailsId = `portal-activity-details-${activity.id}`
                    const compactSummary = [start ? formatActivityStartDate(start, locale) : null, locationName]
                      .filter(Boolean)
                      .join(' · ')
                    return (
                      <ActivityListRow key={activity.id} $expanded={expanded}>
                        <ActivityHeaderRow>
                          <ActivityCollapseButton
                            type="button"
                            aria-expanded={expanded}
                            aria-controls={expanded ? detailsId : undefined}
                            onClick={() => toggleActivity(activity.id)}
                          >
                            <ActivityExpandIcon $expanded={expanded}>
                              <ExpandMore style={{ fontSize: 22 }} aria-hidden />
                            </ActivityExpandIcon>
                            <ActivityCollapseCopy>
                              <ActivityListTitle>{activity.name || getText('portalEnrollments')}</ActivityListTitle>
                              <ActivityStatusPills>
                                <StatusPill $status={activityStatus}>
                                  {getEnrollmentStatusLabel(activityStatus, getText)}
                                </StatusPill>
                                {activityPaid && (
                                  <StatusPill $status={EnrollmentPaymentStatus.Paid}>
                                    {getText(getPaymentStatusTextKey(EnrollmentPaymentStatus.Paid))}
                                  </StatusPill>
                                )}
                              </ActivityStatusPills>
                              {!expanded && compactSummary && (
                                <ActivityCollapsedSummary>{compactSummary}</ActivityCollapsedSummary>
                              )}
                            </ActivityCollapseCopy>
                          </ActivityCollapseButton>
                          <ActivityExternalLink
                            href={getActivityPublicPath(activity, client, lang)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={activity.name || getText('portalEnrollmentOpenActivity')}
                          >
                            <OpenInNew style={{ fontSize: 18 }} aria-hidden />
                          </ActivityExternalLink>
                        </ActivityHeaderRow>
                        {expanded && (
                          <ActivityExpandedDetails id={detailsId}>
                            <ActivityMetaGrid>
                              {start && (
                                <ActivityMetaItem>
                                  <SectionLabel>{getText('portalEnrollmentStartDate')}</SectionLabel>
                                  <ActivityMetaValue>{formatActivityStartDate(start, locale)}</ActivityMetaValue>
                                </ActivityMetaItem>
                              )}
                              {start && (
                                <ActivityMetaItem>
                                  <SectionLabel>{getText('portalEnrollmentStartTime')}</SectionLabel>
                                  <ActivityMetaValue>{formatActivityStartTime(start, locale)}</ActivityMetaValue>
                                </ActivityMetaItem>
                              )}
                              <ActivityMetaItem>
                                <SectionLabel>{getText('portalEnrollmentOccasions')}</SectionLabel>
                                <ActivityMetaValue>
                                  {activity.noOfOccasions || getEnrollmentOccasions(activity).length || 0}
                                </ActivityMetaValue>
                              </ActivityMetaItem>
                              {nextLabel && (
                                <ActivityMetaItem>
                                  <SectionLabel>{getText('portalEnrollmentNextOccasion')}</SectionLabel>
                                  <ActivityMetaValue>{nextLabel}</ActivityMetaValue>
                                </ActivityMetaItem>
                              )}
                              {locationName && (
                                <ActivityMetaItem>
                                  <SectionLabel>{getText('portalEnrollmentLocation')}</SectionLabel>
                                  <LocationText>{locationName}</LocationText>
                                </ActivityMetaItem>
                              )}
                            </ActivityMetaGrid>
                            {hasEnrollmentDescription(activity.description) && (
                              <ActivityRowActions>
                                <OpenDescriptionButton
                                  type="button"
                                  onClick={() => setDescriptionActivityId(activity.id)}
                                >
                                  {getText('portalEnrollmentOpenDescription')}
                                </OpenDescriptionButton>
                              </ActivityRowActions>
                            )}
                          </ActivityExpandedDetails>
                        )}
                      </ActivityListRow>
                    )
                  })}
                </ActivityList>
                {receiptPayments.length > 0 && (
                  <ReceiptsSection aria-label={getText('portalEnrollmentReceipts')}>
                    <ReceiptsSectionHeader>
                      <SectionLabel>{getText('portalEnrollmentReceipts')}</SectionLabel>
                    </ReceiptsSectionHeader>
                    <ReceiptList>
                      {receiptPayments.map((payment, index) => {
                        const receiptKey = getPortalReceiptRowKey(payment, index)
                        const expanded = expandedReceiptKeys.includes(receiptKey)
                        const detailsId = `portal-receipt-details-${receiptKey}`
                        const elementId = `${RECEIPT_ELEMENT_ID}-${receiptKey}`
                        const amountCents = payment.totalAmount ?? 0
                        const netCents =
                          vatRate !== undefined && vatRate > 0 ? Math.round(amountCents / vatMultiplier) : amountCents
                        const vatCents = vatRate !== undefined && vatRate > 0 ? amountCents - netCents : 0
                        const paymentDate = payment.paymentDate
                          ? dayjs(payment.paymentDate).format('D MMM YYYY')
                          : dayjs(enrollment.createdAt).format('D MMM YYYY')
                        const compactSummary = [paymentDate, formatPrice(amountCents, currency), payment.paymentMethod]
                          .filter(Boolean)
                          .join(' · ')
                        return (
                          <ReceiptListRow key={receiptKey} $expanded={expanded}>
                            <ActivityHeaderRow>
                              <ActivityCollapseButton
                                type="button"
                                aria-expanded={expanded}
                                aria-controls={expanded ? detailsId : undefined}
                                onClick={() => toggleReceipt(receiptKey)}
                              >
                                <ActivityExpandIcon $expanded={expanded}>
                                  <ExpandMore style={{ fontSize: 22 }} aria-hidden />
                                </ActivityExpandIcon>
                                <ActivityCollapseCopy>
                                  <ActivityListTitle>
                                    <ReceiptLong
                                      style={{ fontSize: 18, marginRight: 8, verticalAlign: 'text-bottom' }}
                                      aria-hidden
                                    />
                                    {getText('portalEnrollmentReceipt')}
                                  </ActivityListTitle>
                                  {!expanded && <ActivityCollapsedSummary>{compactSummary}</ActivityCollapsedSummary>}
                                </ActivityCollapseCopy>
                              </ActivityCollapseButton>
                            </ActivityHeaderRow>
                            {expanded && (
                              <ActivityExpandedDetails id={detailsId}>
                                <ReceiptActions>
                                  {!isViewAs && (
                                    <SsnToggle type="button" onClick={() => setShowSsnInput(!showSsnInput)}>
                                      <GrooverText
                                        textKey={
                                          displaySsn
                                            ? 'registrationCtactaEditPersonalNumber'
                                            : 'registrationCtactaAddPersonalNumber'
                                        }
                                      />
                                    </SsnToggle>
                                  )}
                                  <DownloadPdfButton type="button" onClick={() => handleDownload(elementId)}>
                                    {getText('portalDownloadPdf')}
                                  </DownloadPdfButton>
                                </ReceiptActions>
                                {!isViewAs && showSsnInput && (
                                  <div style={{ marginBottom: 12 }}>
                                    <ReceiptSsnInputForm
                                      control={ssnFormProps.control}
                                      handleSubmit={ssnFormProps.handleSubmit}
                                      formState={ssnFormProps.formState}
                                      isSaving={isSavingSsn}
                                      onSave={handleSaveSsn}
                                    />
                                  </div>
                                )}
                                <ReceiptBox id={elementId}>
                                  <ReceiptRef>#{enrollment.ocr || enrollment.id}</ReceiptRef>
                                  {client && (client.name || client.orgNo || client.address) && (
                                    <ReceiptSellerBlock>
                                      <ReceiptSellerLabel>{getText('portalReceiptSeller')}</ReceiptSellerLabel>
                                      <ReceiptSellerValue>
                                        {client.name && <span>{client.name}</span>}
                                        {client.orgNo && <span>{client.orgNo}</span>}
                                        {client.address && <span>{client.address}</span>}
                                      </ReceiptSellerValue>
                                    </ReceiptSellerBlock>
                                  )}
                                  <ReceiptRows>
                                    <ReceiptRow>
                                      <ReceiptRowLabel>
                                        {getText('componentsReceiptReceiptPaymentDateLabel')}
                                      </ReceiptRowLabel>
                                      <ReceiptRowValue>{paymentDate}</ReceiptRowValue>
                                    </ReceiptRow>
                                    <ReceiptRow>
                                      <ReceiptRowLabel>{getText('portalReceiptActivity')}</ReceiptRowLabel>
                                      <ReceiptRowValue>{title}</ReceiptRowValue>
                                    </ReceiptRow>
                                    {vatRate !== undefined && vatRate > 0 ? (
                                      <>
                                        <ReceiptRow>
                                          <ReceiptRowLabel>{getText('portalReceiptNetAmountExclVat')}</ReceiptRowLabel>
                                          <ReceiptRowValue>{formatPrice(netCents, currency)}</ReceiptRowValue>
                                        </ReceiptRow>
                                        <ReceiptRow>
                                          <ReceiptRowLabel>
                                            {formatGrooverText(getText('componentsReceiptReceiptPaymentVat'), [
                                              String(vatRate)
                                            ])}
                                          </ReceiptRowLabel>
                                          <ReceiptRowValue>{formatPrice(vatCents, currency)}</ReceiptRowValue>
                                        </ReceiptRow>
                                        <ReceiptRow>
                                          <ReceiptRowLabel>{getText('portalReceiptTotalInclVat')}</ReceiptRowLabel>
                                          <ReceiptRowValue>{formatPrice(amountCents, currency)}</ReceiptRowValue>
                                        </ReceiptRow>
                                      </>
                                    ) : (
                                      <ReceiptRow>
                                        <ReceiptRowLabel>{getText('portalReceiptAmount')}</ReceiptRowLabel>
                                        <ReceiptRowValue>{formatPrice(amountCents, currency)}</ReceiptRowValue>
                                      </ReceiptRow>
                                    )}
                                    {payment.paymentMethod && (
                                      <ReceiptRow>
                                        <ReceiptRowLabel>{getText('portalReceiptPaymentMethod')}</ReceiptRowLabel>
                                        <ReceiptRowValue>{payment.paymentMethod}</ReceiptRowValue>
                                      </ReceiptRow>
                                    )}
                                    {displaySsn && (
                                      <ReceiptRow>
                                        <ReceiptRowLabel>
                                          <GrooverText textKey="receiptPersonalNumberLabel" />
                                        </ReceiptRowLabel>
                                        <ReceiptRowValue>{displaySsn}</ReceiptRowValue>
                                      </ReceiptRow>
                                    )}
                                  </ReceiptRows>
                                </ReceiptBox>
                              </ActivityExpandedDetails>
                            )}
                          </ReceiptListRow>
                        )
                      })}
                    </ReceiptList>
                  </ReceiptsSection>
                )}
              </DetailMainColumn>

              <DetailSideColumn>
                {qrCodeValue && (
                  <DetailQRSection>
                    <span style={{ fontSize: 14, color: '#6b7280' }}>{getText('portalEnrollmentShowQrAtStudio')}</span>
                    <DetailQRWrapper
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowQrExpanded(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setShowQrExpanded(true)
                        }
                      }}
                      aria-label={getText('portalEnrollmentTapToEnlarge')}
                    >
                      <QRCodeElement value={qrCodeValue} size={96} showText={false} />
                    </DetailQRWrapper>
                  </DetailQRSection>
                )}

                {priceLines.length > 0 ? (
                  <>
                    <DetailSideSection>
                      <SectionLabel>{getText('portalEnrollmentPriceBreakdown')}</SectionLabel>
                      {priceLines.map((line) => (
                        <ReceiptBox key={line.activityId} style={{ marginBottom: 12 }}>
                          <ActivityListTitle style={{ marginBottom: 8 }}>{line.activityName}</ActivityListTitle>
                          <ReceiptRows>
                            <ReceiptRow>
                              <ReceiptRowLabel>{getText('portalEnrollmentOriginalPrice')}</ReceiptRowLabel>
                              <ReceiptRowValue>{formatPrice(line.originalPrice, currency)}</ReceiptRowValue>
                            </ReceiptRow>
                            {line.totalDiscountPrice > 0 && (
                              <ReceiptRow>
                                <ReceiptRowLabel>
                                  {line.discountNames.length
                                    ? `${getText('portalEnrollmentDiscount')}: ${line.discountNames.join(', ')}`
                                    : getText('portalEnrollmentDiscount')}
                                </ReceiptRowLabel>
                                <ReceiptRowValue>−{formatPrice(line.totalDiscountPrice, currency)}</ReceiptRowValue>
                              </ReceiptRow>
                            )}
                            {line.administrationFee > 0 && (
                              <ReceiptRow>
                                <ReceiptRowLabel>{getText('portalEnrollmentAdminFee')}</ReceiptRowLabel>
                                <ReceiptRowValue>{formatPrice(line.administrationFee, currency)}</ReceiptRowValue>
                              </ReceiptRow>
                            )}
                            <ReceiptRow>
                              <ReceiptRowLabel>{getText('portalReceiptAmount')}</ReceiptRowLabel>
                              <ReceiptRowValue>{formatPrice(line.finalPrice, currency)}</ReceiptRowValue>
                            </ReceiptRow>
                          </ReceiptRows>
                        </ReceiptBox>
                      ))}
                      <ReceiptBox>
                        <ReceiptRows>
                          <ReceiptRow>
                            <ReceiptRowLabel>{getText('portalEnrollmentToPay')}</ReceiptRowLabel>
                            <ReceiptRowValue>{formatPrice(paymentTotals.total, currency)}</ReceiptRowValue>
                          </ReceiptRow>
                          {paymentTotals.paid > 0 && (
                            <ReceiptRow>
                              <ReceiptRowLabel>{getText('portalEnrollmentPaid')}</ReceiptRowLabel>
                              <ReceiptRowValue $tone="paid">
                                {formatPrice(paymentTotals.paid, currency)}
                              </ReceiptRowValue>
                            </ReceiptRow>
                          )}
                          {paymentTotals.remaining > 0 && (
                            <ReceiptRow>
                              <ReceiptRowLabel>{getText('portalEnrollmentRemaining')}</ReceiptRowLabel>
                              <ReceiptRowValue $tone="remaining">
                                {formatPrice(paymentTotals.remaining, currency)}
                              </ReceiptRowValue>
                            </ReceiptRow>
                          )}
                        </ReceiptRows>
                      </ReceiptBox>
                      {showPayLink && paymentPath && (
                        <PayEnrollmentLinkBlock
                          href={isViewAs ? '#' : paymentPath}
                          target={isViewAs ? undefined : '_blank'}
                          rel={isViewAs ? undefined : 'noopener noreferrer'}
                          onClick={(e) => {
                            if (!handlePortalPayNowClick(e, isViewAs)) {
                              toast.error(getText('portalEnrollmentPayNowReadOnly'), { duration: 4000 })
                            }
                          }}
                        >
                          {getText('portalEnrollmentPayNow')}
                        </PayEnrollmentLinkBlock>
                      )}
                    </DetailSideSection>
                  </>
                ) : (
                  <DetailSideSection>
                    <SectionLabel>{getText('portalEnrollmentPriceBreakdown')}</SectionLabel>
                    <ActivityCollapsedSummary>{getText('portalEnrollmentPriceAfterApproval')}</ActivityCollapsedSummary>
                  </DetailSideSection>
                )}
              </DetailSideColumn>
            </DetailColumns>

            <DetailCloseFooter type="button" onClick={onClose}>
              {getText('portalClose')}
            </DetailCloseFooter>
          </DetailCardContent>
        </DetailCard>
      </DetailOverlay>

      {descriptionActivity && (
        <DescriptionOverlay
          role="dialog"
          aria-modal="true"
          aria-labelledby="portal-enrollment-description-title"
          onClick={() => setDescriptionActivityId(null)}
        >
          <DescriptionModal onClick={(e) => e.stopPropagation()}>
            <DetailCardCloseButton
              type="button"
              onClick={() => setDescriptionActivityId(null)}
              aria-label={getText('portalClose')}
            >
              <Close style={{ fontSize: 20 }} aria-hidden />
            </DetailCardCloseButton>
            <DescriptionModalTitle id="portal-enrollment-description-title">
              {descriptionActivity.name || getText('portalEnrollmentDescription')}
            </DescriptionModalTitle>
            <RichDescription dangerouslySetInnerHTML={{ __html: descriptionActivity.description || '' }} />
          </DescriptionModal>
        </DescriptionOverlay>
      )}

      <QrExpandedDialog
        open={showQrExpanded}
        value={qrCodeValue ?? ''}
        onClose={() => setShowQrExpanded(false)}
        closeLabel={getText('portalClose')}
        instruction={getText('portalEnrollmentShowQrAtStudio')}
        labelledBy="portal-enrollment-detail-qr"
      />
    </>
  )
}
